import React from 'react';
import PropTypes from 'prop-types';
import { StatsCard } from '../../common/StatsCard';
import { getTranslation } from 'common/react/utils/translations';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { useDispatch } from 'react-redux';
import { FeaturedHeadToHeadCardTop } from './FeaturedHeadToHeadCardTop';
import { FeaturedHeadToHeadCardStats } from './FeaturedHeadToHeadCardStats';
import { navLink } from '../../../utils/nav-link';
import { PlayerProfileLink } from '../../common/PlayerProfileLink';
import { playerComparisonActions } from 'common/store/modules/statspro/player-comparison';

/**
 * @param {object} props - Props.
 * @param {Array} props.players - Players details.
 * @param {string} props.position - Players position.
 * @param props.showBenchmarkingAflw
 * @param {boolean} props.isAFLW - if this is an AFLW card
 * @param {string} props.title - Widget title. Used for tracking.
 * @param {string} props.cardIndex - Card index. Used for tracking.
 * @param {string} props.cardsCount - Cards cound in the widget. Used for tracking.
 * @returns {JSX.Element} Component.
 */
export const FeaturedHeadToHeadCard = ({
    players,
    position,
    showBenchmarkingAflw,
    isAFLW = false,
    title,
    cardIndex,
    cardsCount
}) => {
    const dispatch = useDispatch();
    const linkToFullComparison = () => {
        dispatch(
            playerComparisonActions.setPlayer({
                id: players[0].playerId,
                key: 1
            })
        );
        dispatch(
            playerComparisonActions.setPlayer({
                id: players[1].playerId,
                key: 2
            })
        );

        scrollTo({
            top: 0,
            behavior: 'smooth'
        });

        navLink('compare');
    };

    return (
        <StatsCard>
            <FeaturedHeadToHeadCardTop
                players={players}
                position={position}
                isAFLW={isAFLW}
                title={title}
                cardIndex={cardIndex}
                cardsCount={cardsCount}
            />

            <div className="stats-card-table">
                {players.map((player, i) => (
                    <div
                        className="stats-card-table__player-name-container"
                        key={i}
                    >
                        {player?.playerId && (
                            <PlayerProfileLink
                                classes="stats-card-table__player-link"
                                isAFLW={isAFLW}
                                player={{
                                    playerId: player.playerId,
                                    firstName: player.playerDetails.givenName,
                                    surname: player.playerDetails.surname
                                }}
                                trackingInfo={
                                    /* eslint-disable camelcase */
                                    {
                                        component_name: title,
                                        index: cardIndex,
                                        count: cardsCount
                                    }
                                    /* eslint-enable camelcase */
                                }
                            >
                                <div className="stats-card-table__player-name">
                                    {player.playerDetails.givenName}{' '}
                                    <span className="stats-card-table__surname">
                                        {player.playerDetails.surname}
                                    </span>
                                </div>
                            </PlayerProfileLink>
                        )}
                    </div>
                ))}

                <FeaturedHeadToHeadCardStats
                    players={players}
                    position={position}
                    showBenchmarkingAflw={showBenchmarkingAflw}
                />

                <div className="stats-card-table__link-container">
                    <button
                        className="stats-card-table__link"
                        onClick={() => {
                            PULSE.app.tracking.selectPlayerCardHandler(
                                createTrackingNames(players),
                                title ?? 'Stats: Featured Head to Head',
                                PULSE.app.common.CONSTANTS.TRACKING
                                    .CONTENT_TYPES.PLAYER,
                                getTranslation('label.stats.fullComparison'),
                                '/stats/compare',
                                cardIndex,
                                cardsCount
                            );
                            linkToFullComparison();
                        }}
                    >
                        {getTranslation('label.stats.fullComparison')}
                        <span className="stats-card-table__link-icon">
                            <SvgIcon icon="arrow-right" className="icon" />
                        </span>
                    </button>
                </div>
            </div>
        </StatsCard>
    );
};

FeaturedHeadToHeadCard.propTypes = {
    players: PropTypes.array.isRequired,
    position: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    isAFLW: PropTypes.bool,
    title: PropTypes.string,
    cardIndex: PropTypes.number,
    cardsCount: PropTypes.number
};

const createTrackingNames = (playersArray) => {
    const firstName =
        playersArray[0].playerDetails.givenName +
        ' ' +
        playersArray[0].playerDetails.surname;
    const secondName =
        playersArray[1].playerDetails.givenName +
        ' ' +
        playersArray[1].playerDetails.surname;

    return `${firstName} vs ${secondName}`;
};
